import { EkranStartowy, LogoInsolitum } from '@insolitum/ui';
import {
  Activity,
  ArrowLeft,
  AtSign,
  AudioLines,
  Award,
  Banknote,
  BarChart3,
  Boxes,
  Brain,
  Building2,
  Code2,
  Coins,
  CreditCard,
  DollarSign,
  Download,
  Factory,
  HeartPulse,
  Key,
  LayoutDashboard,
  Lightbulb,
  Mail,
  MailCheck,
  Menu,
  MessageSquare,
  Package,
  PanelLeft,
  PanelLeftClose,
  Plug,
  Radar,
  ScrollText,
  Search as SearchIcon,
  ServerCog,
  Settings,
  Shield,
  ShieldAlert,
  Smartphone,
  Sparkles,
  Tablet,
  Ticket,
  TrendingUp,
  Users,
  X,
} from 'lucide-react';
import { Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';

import { ImpersonationBanner } from '../components/ImpersonationBanner';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { UserMenu } from '../components/UserMenu';
import { CommandPalette, useCommandPalette } from '../components/aurora/CommandPalette';
import { useSidebarCollapse } from '../hooks/useSidebarCollapse';
import { useAuth } from '../providers/AuthProvider';

export function SuperAdminLayout() {
  const { t } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { isCollapsed: sidebarCollapsed, toggleCollapse: toggleSidebarCollapse } =
    useSidebarCollapse('superadmin-sidebar-collapsed');
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [paletteOpen, setPaletteOpen] = useCommandPalette();

  // Grupowanie 1:1 z prototypem Aurora DS (VIEW: SUPER-ADMIN): Przegląd →
  // Sprzedaż / Klienci → Organizm AI → Moduły / AI → System. Czysto wizualna
  // kolejność — te same trasy, ikony i klucze pozycji co wcześniej.
  const navigationSections: Array<{
    title: string | null;
    items: Array<{ name: string; href: string; icon: typeof LayoutDashboard; end?: boolean }>;
  }> = [
    {
      title: t('superAdmin.navOverview', 'Przegląd'),
      items: [
        {
          name: t('superAdmin.navDashboard', 'Dashboard'),
          href: '/super-admin',
          icon: LayoutDashboard,
          end: true,
        },
      ],
    },
    {
      title: t('superAdmin.navSalesClients', 'Sprzedaż / Klienci'),
      items: [
        { name: t('superAdmin.tenants', 'Tenanci'), href: '/super-admin/tenants', icon: Building2 },
        {
          // Soft start fazy 1: masowe zakładanie klientów Receptora na planie
          // Free i licencja agenta per serwer (migracja 754).
          name: t('superAdmin.klienciReceptora.tytul', 'Klienci Receptora (faza 1)'),
          href: '/super-admin/klienci-receptora',
          icon: ServerCog,
        },
        {
          name: t('superAdmin.tenantAnalytics', 'Analityka najemców'),
          href: '/super-admin/tenant-analytics',
          icon: BarChart3,
        },
        { name: t('superAdmin.partners', 'Partnerzy'), href: '/super-admin/partners', icon: Users },
        {
          name: t('superAdmin.partnerSettlements', 'Rozliczenia partnerów'),
          href: '/super-admin/partner-settlements',
          icon: Banknote,
        },
        {
          name: t('superAdmin.partnerMailboxes', 'Skrzynki @insolitum.ai'),
          href: '/super-admin/partner-mailboxes',
          icon: AtSign,
        },
        {
          // Certyfikaty modułowe partnerów — kartoteka i unieważnianie.
          // Certyfikat otwiera wejście do organu na koncie klienta (717).
          name: t('superAdmin.partnerCertyfikaty', 'Certyfikaty partnerów'),
          href: '/super-admin/partner-certyfikaty',
          icon: Award,
        },
        { name: t('superAdmin.licenses', 'Licencje'), href: '/super-admin/licenses', icon: Key },
        {
          name: t('superAdmin.pairings', 'Parowania'),
          href: '/super-admin/pairings',
          icon: Smartphone,
        },
        {
          name: t('superAdmin.flotaPaneli', 'Flota paneli'),
          href: '/super-admin/flota-paneli',
          icon: Factory,
        },
        {
          name: t('superAdmin.subscriptions', 'Subskrypcje'),
          href: '/super-admin/subscriptions',
          icon: CreditCard,
        },
      ],
    },
    {
      title: t('superAdmin.navAiOrganism', 'Organizm AI'),
      items: [
        {
          name: t('superAdmin.cockpit', 'Żywa sieć · Kokpit'),
          href: '/super-admin/cockpit',
          icon: Radar,
        },
        {
          name: t('superAdmin.nexusControl', 'Centrum NEXUS'),
          href: '/super-admin/nexus',
          icon: Brain,
        },
        {
          name: t('superAdmin.healthIncidents', 'Incydenty i naprawy'),
          href: '/super-admin/health-incidents',
          icon: ShieldAlert,
        },
        {
          name: t('superAdmin.steeringOpportunities', 'Okazje i sterowanie'),
          href: '/super-admin/steering-opportunities',
          icon: TrendingUp,
        },
        {
          name: t('superAdmin.codeTasks', 'Zadania kodowe'),
          href: '/super-admin/code-tasks',
          icon: Code2,
        },
        {
          name: t('superAdmin.genesisConnectors', 'Źródła wiedzy'),
          href: '/super-admin/genesis-connectors',
          icon: Plug,
        },
        {
          name: t('superAdmin.omniVoiceUpdates', 'Aktualizacje OmniVoice'),
          href: '/super-admin/omnivoice-updates',
          icon: AudioLines,
        },
        {
          name: t('superAdmin.nexusProposals', 'Propozycje NEXUS'),
          href: '/super-admin/nexus-proposals',
          icon: Lightbulb,
        },
        {
          name: t('superAdmin.nexusAssistant', 'Asystent AI'),
          href: '/super-admin/nexus-assistant',
          icon: MessageSquare,
        },
        {
          name: t('superAdmin.assistantUpdates', 'Aktualizacje asystenta'),
          href: '/super-admin/nexus-assistant-updates',
          icon: Download,
        },
      ],
    },
    {
      title: t('superAdmin.navModulesAi', 'Moduły / AI'),
      items: [
        {
          name: t('superAdmin.moduleReview', 'Review modułów'),
          href: '/super-admin/module-review',
          icon: Package,
        },
        {
          name: t('superAdmin.modulesManagement', 'Zarządzanie modułami'),
          href: '/super-admin/modules',
          icon: Boxes,
        },
        {
          name: t('superAdmin.serviceHealth', 'Zdrowie serwisów'),
          href: '/super-admin/service-health',
          icon: HeartPulse,
        },
        {
          name: t('superAdmin.modulePricing', 'Cennik modułów'),
          href: '/super-admin/module-pricing',
          icon: DollarSign,
        },
        {
          name: t('superAdmin.planPricing', 'Cennik planów'),
          href: '/super-admin/plan-pricing',
          icon: Banknote,
        },
        {
          name: t('superAdmin.mobilePricing', 'Cennik mobile/urządzeń'),
          href: '/super-admin/mobile-pricing',
          icon: Tablet,
        },
        {
          name: t('superAdmin.katalogSprzetu', 'Katalog sprzętu'),
          href: '/super-admin/katalog-sprzetu',
          icon: Coins,
        },
        {
          name: t('superAdmin.kartySprzedazowe', 'Karty sprzedażowe'),
          href: '/super-admin/karty-sprzedazowe',
          icon: Sparkles,
        },
      ],
    },
    {
      title: t('superAdmin.navSystem', 'System'),
      items: [
        {
          name: t('superAdmin.auditLog', 'Dziennik'),
          href: '/super-admin/audit-log',
          icon: ScrollText,
        },
        {
          name: t('superAdmin.emailConfig', 'Email'),
          href: '/super-admin/email-config',
          icon: Mail,
        },
        {
          name: t('superAdmin.emailTemplates', 'Szablony e-mail'),
          href: '/super-admin/email-templates',
          icon: MailCheck,
        },
        { name: t('superAdmin.support', 'Wsparcie'), href: '/super-admin/support', icon: Ticket },
        {
          name: t('superAdmin.settings', 'Ustawienia'),
          href: '/super-admin/settings',
          icon: Settings,
        },
        {
          name: t('superAdmin.simulations', 'Symulacje'),
          href: '/super-admin/simulations',
          icon: Activity,
        },
      ],
    },
  ];

  // Okruszki + paleta ⌘K (Aurora DS) — czysto wizualna nawigacja.
  const flatNav = navigationSections.flatMap((s) =>
    s.items.map((i) => ({ ...i, section: s.title ?? undefined }))
  );
  const currentPage =
    location.pathname === '/super-admin'
      ? t('superAdmin.dashboard.title', 'Mission Control')
      : (flatNav
          .filter((i) =>
            i.end ? location.pathname === i.href : location.pathname.startsWith(i.href)
          )
          .sort((a, b) => b.href.length - a.href.length)[0]?.name ??
        t('superAdmin.navDashboard', 'Dashboard'));

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const handleBackToAdmin = () => {
    setSidebarOpen(false);
    navigate('/');
  };

  return (
    <div className="role-super min-h-screen bg-aurora-bg">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-aurora-scrim z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full bg-aurora-bg-subtle border-r border-aurora-border transform transition-all duration-200 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } ${sidebarCollapsed ? 'lg:w-16' : 'lg:w-64'} w-64`}
      >
        <div className="flex flex-col h-full">
          {/* Logo Header */}
          <div className="h-16 flex items-center justify-between px-3 border-b border-aurora-border">
            <div
              className={`flex items-center gap-3 ${sidebarCollapsed ? 'justify-center w-full' : ''}`}
            >
              <span className="sidebar__sygnet">
                {/* Zwinięty pasek nie ma napisu obok — wtedy znak sam niesie nazwę. */}
                <LogoInsolitum
                  uklad="sygnet"
                  tytul={sidebarCollapsed ? 'Insolitum Universe' : null}
                />
              </span>
              {!sidebarCollapsed && (
                <span className="sidebar__id">
                  {/* Nazwa produktu, nie marki: „Insolitum" krojem interfejsu obok
                      sygnetu udawałoby napis znaku (ZNAK-W-SYSTEMIE §3.5). */}
                  <span className="sidebar__name">Universe</span>
                  <span className="sidebar__role">
                    {t('superAdmin.consoleRole', 'Konsola platformy')}
                  </span>
                </span>
              )}
            </div>
            <button
              className="lg:hidden p-2 rounded-ids-md text-aurora-muted hover:bg-aurora-surface hover:text-aurora-text"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Back to Panel */}
          <div className="p-2 border-b border-aurora-border">
            <button
              onClick={handleBackToAdmin}
              className={`navitem ${sidebarCollapsed ? 'justify-center' : ''}`}
              title={sidebarCollapsed ? t('nav.backToPanel', 'Powrót do panelu') : undefined}
            >
              <ArrowLeft className="w-5 h-5 flex-shrink-0" />
              {!sidebarCollapsed && <span>{t('nav.backToPanel', 'Powrót do panelu')}</span>}
            </button>
          </div>

          {/* Navigation — sections mirror the nervous-system "organs" */}
          <nav className="navscroll flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {navigationSections.map((section, sectionIndex) => (
              <div key={section.title ?? `section-${sectionIndex}`}>
                {section.title &&
                  (sidebarCollapsed ? (
                    <div className="my-2 border-t border-aurora-border" />
                  ) : (
                    <p className="navgroup__lbl">{section.title}</p>
                  ))}
                <div className="space-y-1">
                  {section.items.map((item) => (
                    <NavLink
                      key={item.href}
                      to={item.href}
                      end={item.end}
                      onClick={() => setSidebarOpen(false)}
                      className={({ isActive }) =>
                        `navitem ${isActive ? 'is-active' : ''} ${
                          sidebarCollapsed ? 'justify-center' : ''
                        }`
                      }
                      title={sidebarCollapsed ? item.name : undefined}
                    >
                      <item.icon className="w-5 h-5 flex-shrink-0" />
                      {!sidebarCollapsed && <span>{item.name}</span>}
                    </NavLink>
                  ))}
                </div>
              </div>
            ))}
          </nav>

          {/* Collapse Toggle (desktop only) */}
          <div className="hidden lg:block p-2 border-t border-aurora-border">
            <button
              onClick={toggleSidebarCollapse}
              className={`navitem ${sidebarCollapsed ? 'justify-center' : ''}`}
              title={
                sidebarCollapsed
                  ? t('nav.expandMenu', 'Rozwiń menu')
                  : t('nav.collapseMenu', 'Zwiń menu')
              }
            >
              {sidebarCollapsed ? (
                <PanelLeft className="w-5 h-5" />
              ) : (
                <>
                  <PanelLeftClose className="w-5 h-5" />
                  <span>{t('nav.collapseMenu', 'Zwiń menu')}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className={`transition-all duration-200 ${sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-64'}`}>
        {/* Pasek podglądu i topbar przyklejone razem — ten sam układ co
            w PartnerLayout (pasek poza kolumną treści leżał na logo i
            okruszkach). */}
        <div className="sticky top-0 z-30">
          <ImpersonationBanner />
          {/* Top Bar */}
          <header className="topbar z-30">
            <button
              className="iconbtn lg:hidden"
              onClick={() => setSidebarOpen(true)}
              aria-label="Otwórz menu"
            >
              <Menu className="w-5 h-5" />
            </button>

            <div className="topbar__crumbs hidden sm:flex">
              Platforma<span>›</span>
              <b>{currentPage}</b>
            </div>

            <button
              type="button"
              className="cmdk hidden md:flex"
              onClick={() => setPaletteOpen(true)}
              aria-label={t('superAdmin.cmdkPlaceholder', 'Skok do tenanta, partnera, licencji…')}
            >
              <SearchIcon />
              <span>{t('superAdmin.cmdkPlaceholder', 'Skok do tenanta, partnera, licencji…')}</span>
              <kbd>⌘K</kbd>
            </button>

            <div className="flex-1" />

            <div className="topbar__right gap-4">
              {/* .rolebadge — kolor/tło z tokenów roli (.role-super); w prototypie
                siedzi w topbar__right tuż przed avatarem */}
              <span className="rolebadge">
                <Shield className="w-3.5 h-3.5" />
                SUPER ADMIN
              </span>
              <LanguageSwitcher variant="minimal" />
              {/* Jedyne miejsce zalogowanego użytkownika (parytet z AdminLayout) */}
              <UserMenu
                imie={user?.full_name}
                email={user?.email}
                rola={t('roles.super_admin', 'Super Admin')}
                ustawieniaHref="/super-admin/settings"
                onSignOut={handleSignOut}
                inicjalZapasowy="S"
              />
            </div>
          </header>
        </div>

        {/* Page Content */}
        <main className="p-4 lg:p-6">
          {/* Leniwy kawałek strony czeka W TREŚCI: pasek boczny i nagłówek
            zostają. Bez tej granicy czekanie trafiało do korzeniowego
            `Suspense` w `App.tsx`, który zdejmuje cały layout na rzecz
            pełnoekranowego znaku startu — przy każdym pierwszym wejściu
            w trasę z osobnego kawałka. */}
          <Suspense fallback={<EkranStartowy uklad="w-tresci" />}>
            <Outlet />
          </Suspense>
        </main>
      </div>

      {/* Paleta poleceń ⌘K — nawigacja po konsoli platformy (Aurora DS) */}
      <CommandPalette items={flatNav} open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  );
}
